
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const currentLang = i18n.language;

  const toggleLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => location.pathname === path;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}
    >
      <nav className="px-4 md:px-8 lg:px-16 py-4 md:py-6">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 cursor-pointer">
            <img 
              src="https://static.readdy.ai/image/2eafc1b8d0f8a30cd1652506171b568e/fd7c34e53328c1a9d4e2e619c5e8c0e1.jpeg"
              alt="Global Soccer Dreams"
              className="h-8 md:h-10 lg:h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            <Link
              to="/"
              className={`text-base font-bold tracking-wider transition-all duration-300 cursor-pointer relative ${
                isActive('/') 
                  ? 'text-[#0A1628]' 
                  : isScrolled 
                    ? 'text-gray-600 hover:text-[#0A1628]' 
                    : 'text-white hover:text-[#C4FF0E]'
              }`}
            >
              {t('nav.about')}
              {isActive('/') && (
                <span className="absolute -bottom-2 left-0 right-0 h-0.5 bg-[#C4FF0E]"></span>
              )}
            </Link>
            <Link
              to="/services"
              className={`text-base font-bold tracking-wider transition-all duration-300 cursor-pointer relative ${
                isActive('/services') 
                  ? 'text-[#0A1628]' 
                  : isScrolled 
                    ? 'text-gray-600 hover:text-[#0A1628]' 
                    : 'text-white hover:text-[#C4FF0E]'
              }`}
            >
              {t('nav.services')}
              {isActive('/services') && (
                <span className="absolute -bottom-2 left-0 right-0 h-0.5 bg-[#C4FF0E]"></span>
              )}
            </Link>
            <Link
              to="/company"
              className={`text-base font-bold tracking-wider transition-all duration-300 cursor-pointer relative ${
                isActive('/company') 
                  ? 'text-[#0A1628]' 
                  : isScrolled 
                    ? 'text-gray-600 hover:text-[#0A1628]' 
                    : 'text-white hover:text-[#C4FF0E]'
              }`}
            >
              {t('nav.company')}
              {isActive('/company') && (
                <span className="absolute -bottom-2 left-0 right-0 h-0.5 bg-[#C4FF0E]"></span>
              )}
            </Link>
            <Link
              to="/contact"
              className={`px-6 py-2.5 rounded-full font-bold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                isScrolled
                  ? 'bg-[#0A1628] text-white hover:bg-[#1a2d4a]'
                  : 'bg-[#C4FF0E] text-[#0A1628] hover:bg-[#b3ee00]'
              }`}
            >
              {t('nav.contact')}
            </Link>

            {/* Language Switcher */}
            <div className={`flex items-center rounded-full p-1 ${isScrolled ? 'bg-gray-100' : 'bg-white/20'}`}>
              <button
                onClick={() => toggleLanguage('ja')}
                className={`px-3 py-1.5 rounded-full text-sm font-bold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  currentLang === 'ja'
                    ? 'bg-[#C4FF0E] text-[#0A1628]'
                    : isScrolled
                      ? 'text-gray-600 hover:text-[#0A1628]'
                      : 'text-white hover:text-[#C4FF0E]'
                }`}
              >
                日本語
              </button>
              <button
                onClick={() => toggleLanguage('en')}
                className={`px-3 py-1.5 rounded-full text-sm font-bold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  currentLang === 'en'
                    ? 'bg-[#C4FF0E] text-[#0A1628]'
                    : isScrolled
                      ? 'text-gray-600 hover:text-[#0A1628]'
                      : 'text-white hover:text-[#C4FF0E]'
                }`}
              >
                English
              </button>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${
              isScrolled ? 'text-[#0A1628]' : 'text-white'
            }`}
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="lg:hidden fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setIsMobileMenuOpen(false)}
          ></div>
          
          {/* Slide-in Menu */}
          <div className="lg:hidden fixed top-0 right-0 bottom-0 w-72 bg-white shadow-2xl transform transition-transform duration-300 ease-out">
            {/* Close Button */}
            <div className="flex justify-end p-6">
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="w-10 h-10 flex items-center justify-center text-[#0A1628] hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            {/* Language Switcher Mobile */}
            <div className="px-6 mb-4">
              <div className="flex items-center bg-gray-100 rounded-full p-1">
                <button
                  onClick={() => toggleLanguage('ja')}
                  className={`flex-1 px-3 py-2 rounded-full text-sm font-bold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    currentLang === 'ja'
                      ? 'bg-[#C4FF0E] text-[#0A1628]'
                      : 'text-gray-600 hover:text-[#0A1628]'
                  }`}
                >
                  日本語
                </button>
                <button
                  onClick={() => toggleLanguage('en')}
                  className={`flex-1 px-3 py-2 rounded-full text-sm font-bold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    currentLang === 'en'
                      ? 'bg-[#C4FF0E] text-[#0A1628]'
                      : 'text-gray-600 hover:text-[#0A1628]'
                  }`}
                >
                  English
                </button>
              </div>
            </div>

            {/* Menu Items */}
            <div className="flex flex-col px-6 gap-2">
              <Link
                to="/"
                className={`text-base font-bold tracking-wider transition-all duration-300 cursor-pointer py-4 px-4 rounded-lg ${
                  isActive('/') 
                    ? 'bg-[#C4FF0E]/20 text-[#0A1628]' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {t('nav.about')}
              </Link>
              <Link
                to="/services"
                className={`text-base font-bold tracking-wider transition-all duration-300 cursor-pointer py-4 px-4 rounded-lg ${
                  isActive('/services') 
                    ? 'bg-[#C4FF0E]/20 text-[#0A1628]' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {t('nav.services')}
              </Link>
              <Link
                to="/company"
                className={`text-base font-bold tracking-wider transition-all duration-300 cursor-pointer py-4 px-4 rounded-lg ${
                  isActive('/company') 
                    ? 'bg-[#C4FF0E]/20 text-[#0A1628]' 
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {t('nav.company')}
              </Link>
              <Link
                to="/contact"
                className="bg-[#C4FF0E] text-[#0A1628] px-6 py-4 rounded-full font-bold transition-all duration-300 whitespace-nowrap cursor-pointer text-center mt-4 hover:bg-[#b3ee00]"
              >
                {t('nav.contact')}
              </Link>
            </div>
          </div>
        </>
      )}
    </header>
  );
}
